import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Chrome, Mail, Lock, User, Phone, CheckCircle, Crown, Shield, Zap, Check, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import PublicNavbar from "../../components/PublicNavbar";
import CinematicLayout from "../../components/CinematicLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const planIcons = {
  basic: Shield,
  pro: Zap,
  vip: Crown
};

const planColors = {
  basic: { border: "rgba(255, 215, 0, 0.3)", bg: "rgba(255, 215, 0, 0.1)" },
  pro: { border: "rgba(59, 130, 246, 0.5)", bg: "rgba(59, 130, 246, 0.1)" },
  vip: { border: "rgba(168, 85, 247, 0.5)", bg: "rgba(168, 85, 247, 0.1)" }
};

const StaffSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [step, setStep] = useState(1); // 1: Plan selection, 2: Account details
  const [plans, setPlans] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plansLoading, setPlansLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: ""
  });

  // Check if returning from successful payment
  const paymentSuccess = searchParams.get("payment") === "success";
  const sessionId = searchParams.get("session_id");

  const fetchPlans = useCallback(async () => {
    try {
      const response = await fetch(`${API}/membership/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || {});
        // Pre-select basic plan
        setSelectedPlan("basic");
      }
    } catch {
      toast.error("Failed to load membership plans");
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
    
    // If returning from successful payment, skip to step 2
    if (paymentSuccess && sessionId) {
      setStep(2);
      toast.success("Payment successful! Now complete your registration.");
    }
  }, [fetchPlans, paymentSuccess, sessionId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
  };

  const handleProceedToCheckout = async () => {
    if (!selectedPlan) {
      toast.error("Please select a membership plan");
      return;
    }

    if (!form.email.trim()) {
      toast.error("Please enter your email address first");
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await fetch(`${API}/membership/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_id: selectedPlan,
          email: form.email.trim(),
          origin_url: window.location.origin,
          redirect_path: "/staff/signup"  // Redirect back to signup after payment
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Store signup data in sessionStorage before redirecting
        sessionStorage.setItem('pendingSignup', JSON.stringify({
          ...form,
          plan_id: selectedPlan,
          session_id: data.session_id
        }));
        // Redirect to Stripe
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start checkout");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/staff/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          password: form.password,
          phone: form.phone,
          membership_session_id: sessionId // Pass the payment session
        })
      });

      if (response.ok) {
        const user = await response.json();
        setSignupSuccess(true);
        sessionStorage.removeItem('pendingSignup');
        
        if (user.status === "pending") {
          toast.success("Account created! Awaiting admin approval.");
          setTimeout(() => {
            navigate("/staff/pending", { state: { user } });
          }, 2000);
        } else {
          toast.success("Account created successfully! You're the first admin.");
          setTimeout(() => {
            navigate("/dashboard");
          }, 2000);
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || "Signup failed. Please try again.");
      }
    } catch (error) {
      toast.error("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    const url = new URL(`${API}/auth/staff/google`);
    url.searchParams.set("origin_url", window.location.origin);
    window.location.href = url.toString();
  };

  // Success state
  if (signupSuccess) {
    return (
      <CinematicLayout showLogo>
        <PublicNavbar />
        <section className="relative z-10 pt-24 pb-12 px-6 flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="w-full max-w-md">
            <div className="bg-[#121214]/90 backdrop-blur-sm border border-[#22C55E]/30 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-[#22C55E]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-[#22C55E]" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-[#FAFAFA] mb-2">Account Created!</h2>
              <p className="text-[#A1A1AA] mb-4">
                Your application has been submitted. You&apos;ll be redirected shortly...
              </p>
              <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          </div>
        </section>
      </CinematicLayout>
    );
  }

  // Step 1: Membership Plan Selection
  if (step === 1) {
    return (
      <CinematicLayout showLogo>
        <PublicNavbar />
        <section className="relative z-10 pt-20 pb-12 px-6 flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="w-full max-w-4xl">
            <div className="text-center mb-8">
              <Badge className="bg-[#FFD700]/20 text-[#FFD700] mb-4 px-4 py-1">
                <Crown size={14} className="inline mr-2" />
                STEP 1: SELECT MEMBERSHIP
              </Badge>
              <h1 className="text-3xl font-bold text-[#FAFAFA] mb-2">Choose Your Plan</h1>
              <p className="text-[#A1A1AA]">A membership is required to join the DopeInHD platform</p>
            </div>

            {plansLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#FFD700] animate-spin" />
              </div>
            ) : (
              <>
                {/* Plans Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {Object.entries(plans).map(([planId, plan]) => {
                    const Icon = planIcons[planId] || Shield;
                    const colors = planColors[planId] || planColors.basic;
                    const isSelected = selectedPlan === planId;
                    const isPopular = planId === "pro";
                    
                    return (
                      <Card 
                        key={planId}
                        className={`relative cursor-pointer transition-all duration-300 hover:scale-[1.02] ${isSelected ? 'ring-2 ring-[#FFD700]' : ''}`}
                        style={{
                          background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.95) 0%, rgba(15, 10, 5, 0.95) 100%)',
                          backdropFilter: 'blur(20px)',
                          border: `2px solid ${isSelected ? '#FFD700' : colors.border}`,
                          boxShadow: isSelected ? '0 0 30px rgba(255, 215, 0, 0.2)' : 'none'
                        }}
                        onClick={() => handlePlanSelect(planId)}
                        data-testid={`plan-${planId}`}
                      >
                        {isPopular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <Badge className="bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] px-3 text-xs">
                              POPULAR
                            </Badge>
                          </div>
                        )}
                        
                        <CardHeader className="text-center pt-6 pb-2">
                          <div 
                            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                            style={{ background: colors.bg }}
                          >
                            <Icon size={24} className="text-[#FFD700]" />
                          </div>
                          <CardTitle className="text-lg text-[#FFFFFF]">
                            {plan.name}
                          </CardTitle>
                          <div className="mt-2">
                            <span className="text-3xl font-bold text-[#FFD700]">${plan.price}</span>
                            <span className="text-[#E5C558] text-sm">/month</span>
                          </div>
                        </CardHeader>
                        
                        <CardContent className="pt-0">
                          <ul className="space-y-2">
                            {plan.features?.slice(0, 4).map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-[#E5C558] text-sm">
                                <Check size={14} className="text-[#22C55E] flex-shrink-0 mt-0.5" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                          
                          <Button
                            className={`w-full mt-4 ${isSelected 
                              ? 'bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500]' 
                              : 'bg-transparent border border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePlanSelect(planId);
                            }}
                            size="sm"
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Email + Continue */}
                <div className="bg-[#121214]/90 backdrop-blur-sm border border-[#27272A] rounded-xl p-6 max-w-md mx-auto">
                  <div className="mb-4">
                    <Label className="text-[#A1A1AA]">Email Address</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                      <Input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                        placeholder="you@example.com"
                        data-testid="signup-email"
                      />
                    </div>
                    <p className="text-xs text-[#71717A] mt-1">We&apos;ll use this for your membership and account</p>
                  </div>

                  <Button
                    onClick={handleProceedToCheckout}
                    disabled={!selectedPlan || !form.email.trim() || checkoutLoading}
                    className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8952F] text-[#0A0A0B] font-semibold hover:from-[#E5C558] hover:to-[#D4AF37]"
                    data-testid="proceed-to-payment"
                  >
                    {checkoutLoading ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={18} />
                        Redirecting to payment...
                      </>
                    ) : (
                      <>
                        Continue to Payment
                        <ArrowRight className="ml-2" size={18} />
                      </>
                    )}
                  </Button>

                  <p className="text-center text-xs text-[#71717A] mt-4">
                    Secure payment powered by Stripe
                  </p>
                </div>

                <p className="mt-6 text-center text-sm text-[#A1A1AA]">
                  Already have an account?{" "}
                  <Link to="/staff/login" className="text-[#D4AF37] hover:underline">
                    Sign in
                  </Link>
                </p>
              </>
            )}
          </div>
        </section>
      </CinematicLayout>
    );
  }

  // Step 2: Account details (after successful payment)
  return (
    <CinematicLayout showLogo>
      <PublicNavbar />

      <section className="relative z-10 pt-24 pb-12 px-6 flex items-center justify-center min-h-[calc(100vh-80px)]">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Badge className="bg-[#22C55E]/20 text-[#22C55E] mb-4 px-4 py-1">
              <CheckCircle size={14} className="inline mr-2" />
              PAYMENT COMPLETE
            </Badge>
            <h1 className="text-3xl font-bold text-[#FAFAFA] mb-2">Complete Registration</h1>
            <p className="text-[#A1A1AA]">Create your account credentials</p>
          </div>

          <div className="bg-[#121214]/90 backdrop-blur-sm border border-[#27272A] rounded-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-[#A1A1AA]">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                  <Input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    placeholder="John Doe"
                    data-testid="signup-name"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA]">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                  <Input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    placeholder="you@example.com"
                    data-testid="signup-email-step2"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA]">Phone (Optional)</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                  <Input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    placeholder="+1 (555) 000-0000"
                    data-testid="signup-phone"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA]">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                  <Input
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    placeholder="••••••••"
                    data-testid="signup-password"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA]">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                  <Input
                    name="confirmPassword"
                    type="password"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    placeholder="••••••••"
                    data-testid="signup-confirm"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8952F] text-[#0A0A0B] font-semibold hover:from-[#E5C558] hover:to-[#D4AF37]"
                data-testid="signup-submit"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#27272A]"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-[#121214] px-2 text-[#71717A]">or continue with</span>
              </div>
            </div>

            <Button
              onClick={handleGoogleSignup}
              variant="outline"
              className="w-full border-[#27272A] text-[#FAFAFA] hover:bg-[#1A1A1D]"
              data-testid="google-signup"
            >
              <Chrome size={20} className="mr-2" />
              Sign up with Google
            </Button>

            <p className="mt-6 text-center text-sm text-[#A1A1AA]">
              Already have an account?{" "}
              <Link to="/staff/login" className="text-[#D4AF37] hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </section>
    </CinematicLayout>
  );
};

export default StaffSignup;
